// Plan data → view models for the static Phase-1 surfaces.
//
// TWO RULES SHAPE THIS FILE.
//
// 1. NO SURFACE HARDCODES A STATE. Every "Needs someone / Covered / Please
//    recheck / Done / Cancelled" on screen comes out of `deriveCoverage` in the
//    domain kernel. If the kernel's precedence changes, these screens change
//    with it — which is the point of building the demo on the real engine rather
//    than on a hand-written list of states.
//
// 2. THE DATA IS AN ARGUMENT, NOT AN IMPORT. This module takes a `PlanSource`
//    and never reaches for a data provider itself. Two reasons, and both matter:
//    `no-fixtures-in-production` forbids `@herefor/test-fixtures` reaching
//    production code (RULE 2's mechanical guard), and Epic 3 has to swap
//    fixtures for reactive Convex queries "with minimal UI churn" — which only
//    works if nothing here knows where the data came from.

import {
  deriveCoverage,
  localDateOf,
  toWallClock,
  type AvailabilityWindow,
  type Commitment,
  type DerivedCoverage,
  type EventRecord,
  type Instant,
  type PersonId,
  type SupportNeed,
  type TimeZoneId,
} from "@herefor/domain-kernel";
import { USER_VISIBLE_STATES, type UserVisibleState } from "@herefor/product-language";

/** Everything the surfaces need, from wherever it came. */
export interface PlanSource {
  readonly timeZone: TimeZoneId;
  readonly now: Instant;
  readonly events: readonly EventRecord[];
  readonly supportNeeds: readonly SupportNeed[];
  readonly commitments: readonly Commitment[];
  readonly availabilityWindows: readonly AvailabilityWindow[];
  /** Display names, by person. Chosen names only — never a real identifier. */
  readonly names: ReadonlyMap<PersonId, string>;
  readonly activePersonIds: ReadonlySet<PersonId>;
}

/**
 * Kernel status → product-language key.
 *
 * An exhaustive record rather than a lookup with a fallback: a new kernel status
 * must not be able to render silently as "Needs someone".
 */
const STATE_KEY: Record<DerivedCoverage["status"], UserVisibleState> = {
  needs_someone: "needsSomeone",
  covered: "covered",
  please_recheck: "pleaseRecheck",
  done: "done",
  cancelled: "cancelled",
};

export interface ResponsibleView {
  readonly personId: PersonId;
  readonly name: string;
  /** Non-null only for a proxy record (COM-009, invariant 19). */
  readonly recordedByName: string | null;
  readonly agreementSource: string | null;
}

export interface NeedView {
  readonly id: string;
  readonly label: string;
  /**
   * Present for live records, and always contains opaque identifiers only.
   *
   * A need is where somebody offers to help (bead hf-e6-commitments-coverage-udt.2),
   * so it needs a route to reach. The fixture-backed surfaces have none and render
   * the label plainly.
   */
  readonly href?: string;
  readonly stateKey: UserVisibleState;
  readonly stateLabel: string;
  readonly stateExplanation: string;
  /**
   * Raw counts. NOT a sentence: `STATUS_CONTEXT.coverageCount` owns the words
   * (and the person/people pluralisation), so this module never composes copy.
   * Null when the need wants a single person, so a simple visit does not sprout
   * "1 of 1 person confirmed".
   */
  readonly counts: { readonly confirmed: number; readonly required: number } | null;
  readonly windowLabel: string;
  readonly dateLabel: string;
  readonly isPast: boolean;
  readonly startsAt: Instant;
  readonly responsible: readonly ResponsibleView[];
}

export interface EventView {
  readonly id: string;
  readonly title: string;
  /** Present for live records, and always contains opaque identifiers only. */
  readonly href?: string;
  readonly windowLabel: string | null;
  readonly dateLabel: string | null;
  readonly locationLabel: string | null;
  /**
   * When it begins, for ordering only — never for display; the labels above own
   * that. Null for a draft with no time yet, which sorts after everything dated
   * (bead hf-qyq, where the week list learned to carry standalone needs too).
   */
  readonly startsAt: Instant | null;
  /** Only terminal event states use a primary user-visible status chip. */
  readonly stateKey: "done" | "cancelled" | null;
  readonly stateLabel: string | null;
  readonly needs: readonly NeedView[];
}

export interface AvailabilityRowView {
  readonly key: string;
  readonly name: string;
  readonly dateLabel: string;
  readonly windowLabel: string;
  readonly helpModes: readonly string[];
  readonly recordedByName: string | null;
}

/** `09:15–13:00` in the circle's zone. Never naive UTC arithmetic (invariant 9). */
export function formatWindow(source: PlanSource, startsAt: Instant, endsAt: Instant): string {
  return formatWindowInZone(source.timeZone, startsAt, endsAt);
}

/** Format one complete window in the circle's IANA zone. */
export function formatWindowInZone(
  timeZone: TimeZoneId,
  startsAt: Instant,
  endsAt: Instant,
): string {
  const at = (instant: Instant) => {
    const w = toWallClock(instant, timeZone);
    return `${String(w.hour).padStart(2, "0")}:${String(w.minute).padStart(2, "0")}`;
  };
  return `${at(startsAt)}–${at(endsAt)}`;
}

/**
 * The Agenda's day heading — `SAT 29 AUG` (plan §14.4, UX-006).
 *
 * TAKES A `LocalDate`, WHICH IS ALREADY A WALL-CLOCK DAY. The zone was applied when
 * `localDateOf` produced it; applying one again here is how a 23:30 need on the last
 * Sunday in October ends up under the wrong heading. So the string is parsed at NOON
 * UTC and formatted in UTC — noon because it is the furthest any offset can be from
 * rolling the date, and UTC because a `LocalDate` has no zone left to honour.
 *
 * Uppercase because §14.4 writes it that way, and short because a heading repeated
 * down a scrolling list earns its brevity. The date itself is not private content —
 * it says a day exists, not what is on it.
 */
export function formatAgendaDayHeading(localDate: string): string {
  const at = new Date(`${localDate}T12:00:00Z`);
  if (Number.isNaN(at.getTime())) return localDate;

  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  })
    .format(at)
    .replace(/,/gu, "")
    .toUpperCase();
}

/** Kernel status -> the product-language state key used by every plan surface. */
export function userVisibleStateKey(status: DerivedCoverage["status"]): UserVisibleState {
  return STATE_KEY[status];
}

function nameOf(source: PlanSource, personId: PersonId): string {
  return source.names.get(personId) ?? String(personId);
}

function responsibleFor(
  source: PlanSource,
  commitments: readonly Commitment[],
): readonly ResponsibleView[] {
  return commitments
    .filter((c) => c.state === "accepted" || c.state === "reconfirmation_required")
    .map((c) => ({
      personId: c.responsiblePersonId,
      name: nameOf(source, c.responsiblePersonId),
      // A proxy record must disclose its recorder and source wherever the
      // commitment is shown, not only in an audit view.
      recordedByName: c.origin === "proxy" ? nameOf(source, c.recordedByPersonId) : null,
      agreementSource: c.origin === "proxy" ? c.agreementSource : null,
    }));
}

export function toNeedView(source: PlanSource, need: SupportNeed): NeedView {
  const commitments = source.commitments.filter((c) => c.supportNeedId === need.id);
  const linkedEvent = source.events.find((e) => e.id === need.eventId);

  const coverage = deriveCoverage({
    need,
    commitments,
    activePersonIds: source.activePersonIds,
    now: source.now,
    ...(linkedEvent === undefined ? {} : { currentEventRevision: linkedEvent.revision }),
  });

  const stateKey = userVisibleStateKey(coverage.status);
  const copy = USER_VISIBLE_STATES[stateKey];

  return {
    id: String(need.id),
    label: need.label,
    stateKey,
    stateLabel: copy.label,
    stateExplanation: copy.explanation,
    // NED-012, COV-006: a count of PEOPLE, never a percentage. The words are
    // product-language's; this only supplies the numbers.
    counts:
      coverage.requiredCapacity > 1
        ? { confirmed: coverage.confirmedCount, required: coverage.requiredCapacity }
        : null,
    windowLabel: formatWindow(source, need.startsAt, need.endsAt),
    dateLabel: localDateOf(need.startsAt, source.timeZone),
    isPast: coverage.isPast,
    startsAt: need.startsAt,
    responsible: responsibleFor(source, commitments),
  };
}

export function allNeedViews(source: PlanSource): readonly NeedView[] {
  return source.supportNeeds.map((need) => toNeedView(source, need));
}

/**
 * Needs that belong to no event, in their own right (bead hf-qyq).
 *
 * A need's window is independent of any event's (NED-002), and a need with no
 * event at all is an ordinary thing a circle asks for — "someone to sit with her
 * on Thursday". Its only home used to be the attention list, which keeps just the
 * two states that still want a person, so covering one removed it from the app.
 */
export function standaloneNeedViews(source: PlanSource): readonly NeedView[] {
  return source.supportNeeds
    .filter((need) => need.eventId === undefined)
    .map((need) => toNeedView(source, need))
    .slice()
    .sort((left, right) => left.startsAt - right.startsAt);
}

/**
 * Events with their linked needs.
 *
 * Need windows are formatted from the NEED, independently of the event's
 * (NED-002, EVT-005). A driver needed 09:15–13:00 around a 10:15 appointment
 * must read as 09:15–13:00, or somebody turns up at the wrong time.
 */
export function allEventViews(source: PlanSource): readonly EventView[] {
  return source.events.map((event) => ({
    id: String(event.id),
    title: event.title,
    windowLabel:
      event.startsAt !== undefined && event.endsAt !== undefined
        ? formatWindow(source, event.startsAt, event.endsAt)
        : null,
    dateLabel: event.startsAt === undefined ? null : localDateOf(event.startsAt, source.timeZone),
    locationLabel: event.locationLabel ?? null,
    startsAt: event.startsAt ?? null,
    stateKey:
      event.state === "completed" ? "done" : event.state === "cancelled" ? "cancelled" : null,
    stateLabel:
      event.state === "completed"
        ? USER_VISIBLE_STATES.done.label
        : event.state === "cancelled"
          ? USER_VISIBLE_STATES.cancelled.label
          : null,
    needs: source.supportNeeds
      .filter((n) => n.eventId === event.id)
      .map((n) => toNeedView(source, n)),
  }));
}

/**
 * Needs wanting a human's attention, most urgent first.
 *
 * "Please recheck" ranks above "Needs someone": somebody already agreed to the
 * first and is waiting to be asked again, which is a promise at risk rather than
 * a gap still to fill. Done and Cancelled ask nothing of anybody and are absent.
 */
export function needsAttention(source: PlanSource): readonly NeedView[] {
  return sortNeedsAttention(allNeedViews(source));
}

/** Apply the one canonical attention filter/order to already-derived live views. */
export function sortNeedsAttention(views: readonly NeedView[]): readonly NeedView[] {
  const rank: Partial<Record<UserVisibleState, number>> = { pleaseRecheck: 0, needsSomeone: 1 };
  return views
    .filter((v) => rank[v.stateKey] !== undefined)
    .slice()
    .sort((a, b) => (rank[a.stateKey] ?? 9) - (rank[b.stateKey] ?? 9) || a.startsAt - b.startsAt);
}

/**
 * Offered availability, for "who could help".
 *
 * Only windows a person actually offered. `away` and `withdrawn` are excluded,
 * and an absent window is simply absent — unmarked means Not set, never
 * "unavailable" (AVL-009, invariant 13).
 */
export function availabilityRows(source: PlanSource): readonly AvailabilityRowView[] {
  return source.availabilityWindows
    .filter((w) => w.availabilityState === "available")
    .slice()
    .sort((a, b) => a.startsAt - b.startsAt)
    .map((w) => ({
      key: String(w.id),
      name: nameOf(source, w.personId),
      dateLabel: localDateOf(w.startsAt, source.timeZone),
      windowLabel: formatWindow(source, w.startsAt, w.endsAt),
      helpModes: w.helpModes,
      recordedByName:
        w.source === "proxy" && w.recordedByPersonId !== undefined
          ? nameOf(source, w.recordedByPersonId)
          : null,
    }));
}
